import React from "react";
import { Box, Grid, styled, Typography } from "@mui/material";
import { useGetListWebinairesQuery } from "../../../../store/api/odyssee";
import useOdyssee from "../../../../context/odyssee/useOdyssee";
import { useAuthToken } from "../../../../hooks/auth";
import { useOdysseeTheme } from "../../../../context/odysseeTheme/OdysseeThemeProvider";
import LiveCard from "./Lives/LiveCard";
import FutureLiveCard from "./Lives/FutureLiveCard";
import moment from "moment";
import { Dialog, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";

const ContentCard = styled(Box)(() => ({
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: 20,
    padding: 30,
}));

const SectionContainer = styled(Box)(() => ({
    marginBottom: 32,
    "&:last-child": {
        marginBottom: 0,
    },
}));

const Lives = () => {
    const { odyssee } = useOdyssee();
    const authToken = useAuthToken();
    const { data } = useGetListWebinairesQuery(
        { odysseeId: odyssee?.id, authToken },
        { skip: !odyssee?.id }
    );

    const [titleColor, contentColor] = useOdysseeTheme("card", [
        "header.title",
        "header.content",
    ]);

    const [activeVideoUrl, setActiveVideoUrl] = React.useState(null);

    const handleCardClick = (url) => {
        if (url) {
            setActiveVideoUrl(url);
        }
    };

    const handleClose = () => {
        setActiveVideoUrl(null);
    };


    const now = moment();

    const futures = React.useMemo(() => {
        return (data?.futures || []).filter((webinaire) => {
            if (!webinaire.visibleAt) return true;
            return now.isSameOrAfter(moment(webinaire.visibleAt * 1000));
        });
    }, [data?.futures]);

    const pasts = React.useMemo(() => {
        return (data?.pasts || []).filter((webinaire) => {
            if (!webinaire.visibleAt) return true;
            return now.isSameOrAfter(moment(webinaire.visibleAt * 1000));
        });
    }, [data?.pasts]);

    const hasFutures = futures.length > 0;
    const hasPasts = pasts.length > 0;

    if (!hasFutures && !hasPasts) {
        return (
            <ContentCard sx={{ textAlign: "center", py: 4 }}>
                <Typography
                    color={contentColor || "rgba(255,255,255,0.5)"}
                    fontFamily="Branding"
                    fontSize={16}
                >
                    Aucun direct disponible pour le moment
                </Typography>
            </ContentCard>
        );
    }

    return (
        <>
            <ContentCard>
                {hasFutures && (
                    <SectionContainer>
                        <Typography
                            color={titleColor || "#ffffff"}
                            fontFamily="Branding"
                            fontWeight={700}
                            fontSize={24}
                            mb={2.5}
                        >
                            Prochains webinaires : soyez au rendez-vous
                        </Typography>
                        <Grid container spacing={3}>
                            {futures.map((webinaire) => (
                                <Grid item xs={12} sm={6} lg={6} key={webinaire._id}>
                                    <FutureLiveCard
                                        webinaire={webinaire}
                                        onPlayClick={() => handleCardClick(webinaire.iframeUrl)}
                                    />
                                </Grid>
                            ))}
                        </Grid>
                    </SectionContainer>
                )}
                {hasPasts && (
                    <SectionContainer>
                        <Typography
                            color={titleColor || "#ffffff"}
                            fontFamily="Branding"
                            fontWeight={700}
                            fontSize={24}
                            mb={2.5}
                        >
                            À revoir dans l'odyssée
                        </Typography>
                        <Box
                            sx={{
                                display: "flex",
                                flexWrap: "wrap",
                                gap: 3,
                                justifyContent: { xs: "center", sm: "flex-start" },
                            }}
                        >
                            {pasts.map((webinaire) => (
                                <Box
                                    key={webinaire._id}
                                    sx={{
                                        width: { xs: "100%", sm: "auto" },
                                        maxWidth: { xs: "100%", sm: 300 },
                                        minWidth: { xs: "100%", sm: 200 },
                                        flex: { xs: "1 1 100%", sm: "0 1 auto" },
                                        display: "flex",
                                        justifyContent: "center",
                                    }}
                                >
                                    <LiveCard
                                        webinaire={webinaire}
                                        isPast
                                        onPlayClick={() => handleCardClick(webinaire.replayUrl)}
                                    />
                                </Box>
                            ))}
                        </Box>
                    </SectionContainer>
                )}
            </ContentCard>

            <Dialog
                fullScreen={window.innerWidth < 600}
                open={!!activeVideoUrl}
                onClose={handleClose}
                maxWidth="xl"
                fullWidth
                PaperProps={{
                    sx: {
                        backgroundColor: "black",
                        backgroundImage: "none",
                        overflow: "hidden",
                        maxWidth: "90vw", // Force it to be wide
                        maxHeight: "90vh",
                        width: "100%",
                        margin: 0,
                    },
                }}
            >
                <IconButton
                    aria-label="close"
                    onClick={handleClose}
                    sx={{
                        position: "absolute",
                        right: 8,
                        top: 8,
                        color: (theme) => theme.palette.grey[500],
                        zIndex: 1,
                    }}
                >
                    <CloseIcon />
                </IconButton>
                {activeVideoUrl && (
                    <Box
                        sx={{
                            position: "relative",
                            paddingBottom: "56.25%", // 16:9 aspect ratio
                            height: 0,
                            overflow: "hidden",
                        }}
                    >
                        <iframe
                            src={activeVideoUrl}
                            title="Video Player"
                            style={{
                                position: "absolute",
                                top: 0,
                                left: 0,
                                width: "100%",
                                height: "100%",
                                border: 0,
                            }}
                            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                            allowFullScreen
                        />
                    </Box>
                )}
            </Dialog>
        </>
    );
};

export default Lives;